import React, { useEffect, useState } from "react";
import { config } from "../config/app.config";
import { useLocation } from "react-router-dom";
import { Box, Button, IconPrint, Text, Actions, Alert } from "braid-design-system";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { formatPrintDate } from "../utils/dateUtils";
import { useAuth } from "../contexts/AuthProvider";
import { getFdRenewalLetter, FdRenewalLetterResponse } from "../services/fdRenewalLetterService";

type Lang = "en" | "si";

const labels: { [key: string]: Record<Lang, string> } = {
    title: {
        en: "FIXED DEPOSIT RENEWAL NOTICE",
        si: "ස්ථාවර තැන්පත් අළුත් කිරීමේ නිවේදනය",
    },
    memberNumber: {
        en: "Member Number",
        si: "සාමාජික අංකය",
    },
    customerName: {
        en: "Name",
        si: "නම",
    },
    address: {
        en: "Address",
        si: "ලිපිනය",
    },
    salutation: {
        en: "Dear Sir / Madam,",
        si: "මහත්මයාණෙනි / මහත්මියනි,",
    },
    prevHeader: {
        en: "PREVIOUS FIXED DEPOSIT DETAILS",
        si: "පැවති ස්ථාවර තැන්පතු විස්තර",
    },
    branch: {
        en: "Branch",
        si: "ශාඛාව",
    },
    accountNo: {
        en: "Account Number",
        si: "ගිණුම් අංකය",
    },
    depositAmount: {
        en: "Deposit Amount",
        si: "තැන්පතු මුදල",
    },
    maturityDate: {
        en: "Maturity Date",
        si: "කල් පිරෙන දිනය",
    },
    prevRate: {
        en: "Previous Interest Rate",
        si: "පැවති පොළි අනුපාතය",
    },
    period: {
        en: "Deposit Period (Months)",
        si: "තැන්පතු කාල සීමාව (මාස)",
    },
    totalInterest: {
        en: "Total Interest Paid",
        si: "ගෙවන ලද මුළු පොළිය",
    },
    withholdingTax: {
        en: "Withholding Tax",
        si: "රදවා ගැනීමේ බදු මුදල",
    },
    netInterest: {
        en: "Net Interest Paid",
        si: "ගෙවන ලද ශුද්ධ පොළිය",
    },
    introParagraph: {
        en: "We are pleased to inform you that your deposit has been renewed as follows, in accordance with the instructions given at the time of opening the account.",
        si: "ඔබගේ තැන්පතු මුදල ඔබ ගිණුම විවෘත කරන අවස්ථාවේදී ලබා දුන් උපදෙස් අනුව පහත පරිදි අළුත් කළ බව ඔබ වෙත සතුටින් දැනුම් දෙමු.",
    },
    renewHeader: {
        en: "RENEWED FIXED DEPOSIT DETAILS",
        si: "අළුත් කළ ස්ථාවර තැන්පතු විස්තර",
    },
    currentValue: {
        en: "Current Value of Deposit",
        si: "තැන්පතුවේ වර්තමාන වටිනාකම",
    },
    newAccountNo: {
        en: "New Account Number",
        si: "නව ගිණුම් අංකය",
    },
    renewPeriod: {
        en: "Deposit Period (Months)",
        si: "තැන්පතු කාල සීමාව (මාස)",
    },
    newRate: {
        en: "New Interest Rate",
        si: "නව පොළි අනුපාතය",
    },
    interestAtMaturity: {
        en: "Interest Payable at Maturity",
        si: "තැන්පතුව කල් පිරීමේදී ගෙවිය යුතු පොළිය",
    },
    renewDate: {
        en: "Renewal Date",
        si: "අළුත් කරන දිනය",
    },
    branchTelephone: {
        en: "Branch Telephone",
        si: "ශාඛාවේ දුරකථන අංකය",
    },
    taxParagraphPrefix: {
        en: "The withholding tax amount of Rs. ",
        si: "අදාල කාල පරිච්ඡේදය සඳහා වන රදවා ගැනීමේ බදු මුදල වන රු. ",
    },
    taxParagraphSuffix: {
        en: " for the relevant period has been remitted to the Department of Inland Revenue.",
        si: " ක මුදල දේශීය ආදායම් බදු දෙපාර්තමේන්තුව වෙත ප්‍රේශණය කළ බව මෙයින් ඔබ වෙත දැනුම් දෙමු.",
    },
    thanks: {
        en: "Thank You",
        si: "ස්තූතියි",
    },
    yoursFaithfully: {
        en: "Yours faithfully,",
        si: "ස්තූතියි. \nමෙයට,",
    },
    manager: {
        en: "Manager",
        si: "කළමණාකරු",
    },
    page: {
        en: "Page",
        si: "පිටුව",
    },
    of: {
        en: "of",
        si: "න්",
    },
    user: {
        en: "User",
        si: "පරිශීලක",
    },
    printButton: {
        en: "Print Renewal Letter",
        si: "අළුත් කිරීමේ ලිපිය මුද්‍රණය කරන්න",
    },
    noData: {
        en: "No FD renewal letter data found for this account.",
        si: "මෙම ගිණුම සඳහා අළුත් කිරීමේ ලිපි දත්ත හමු නොවීය.",
    },
};

const FdRenewalLetterPrint: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const accountId = Number(queryParams.get("accountId"));

    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<FdRenewalLetterResponse | null>(null);
    const [error, setError] = useState<string>("");
    const [printLang, setPrintLang] = useState<Lang>("si");
    const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            if (!accountId) {
                setError("Missing accountId.");
                setLoading(false);
                return;
            }
            setLoading(true);
            const res = await getFdRenewalLetter(accountId);
            if (!res) {
                setError("Failed to load FD renewal letter data.");
            } else {
                setData(res);
            }
            setLoading(false);
        };
        fetchData();
    }, [accountId]);

    useEffect(() => {
        const instLogo = data?.institute?.logo;
        if (instLogo) {
            if (instLogo.startsWith("data:") || instLogo.startsWith("http")) {
                setLogoUrl(instLogo);
            } else {
                const cleanedPath = instLogo.startsWith("/") ? instLogo : `/${instLogo}`;
                if (cleanedPath.startsWith("/uploads/")) {
                    setLogoUrl(`${config.apiUrl.replace(/\/api\/?$/, "")}${cleanedPath.replace("/uploads/", "/api/uploads/")}`);
                } else {
                    setLogoUrl(`${config.apiUrl.replace(/\/api\/?$/, "")}/api/uploads/${instLogo.replace("uploads/", "")}`);
                }
            }
        } else {
            setLogoUrl(undefined);
        }
    }, [data]);

    const t = (key: string) => labels[key]?.[printLang] || labels[key]?.["en"] || "";

    const fmtDate = (value?: string | null) => (value ? formatPrintDate(value, "yyyy-MM-dd") : "-");

    if (loading) {
        return <Loading message="Loading FD renewal letter data..." />;
    }

    if (error || !data) {
        return (
            <Box padding="large">
                <Alert tone="critical">
                    <Text weight="strong">{error || t("noData")}</Text>
                </Alert>
            </Box>
        );
    }

    const inst = data.institute || {};
    const branch = data.branch || {};
    const customer = data.customer || {};
    const prev = data.previous || {};
    const renewal = data.renewal || {};

    const instName = (printLang === "si" ? inst.name_ln2 : inst.name_ln1) || inst.name_ln1 || "";
    const instAddress = (printLang === "si" ? inst.address_ln2 : inst.address_ln1) || inst.address_ln1 || "";
    const branchName = (printLang === "si" ? branch.name_ln2 : branch.name_ln1) || branch.name_ln1 || "";
    const custName = (printLang === "si" ? customer.full_name_ln2 : customer.full_name_ln1) || customer.full_name_ln1 || "";
    const custAddress = (printLang === "si" ? customer.address_ln2 : customer.address_ln1) || customer.address_ln1 || "";

    const taxAmount = prev.tax;
    const todayStr = formatPrintDate(new Date(), "dd-MM-yyyy");
    const userName = user?.user_name || "";

    const renderDetailTable = (rows: { label: string; value: string }[]) => (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", margin: "8px 0 14px 0" }}>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i}>
                        <td style={{ width: "50%", padding: "5px 8px", border: "1px solid #d1d5db", fontWeight: "bold", backgroundColor: "#f8fafc" }}>{row.label}</td>
                        <td style={{ width: "50%", padding: "5px 8px", border: "1px solid #d1d5db", textAlign: "right" }}>{row.value}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <>
            <Box padding="medium">
                <style>{`
                    @media print {
                        .no-print {
                            display: none !important;
                        }
                        body {
                            background-color: #fff !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        @page {
                            size: A4 portrait;
                            margin: 0.6cm 0.6cm 0.6cm 1.5cm;
                        }
                        #print-page {
                            position: absolute !important;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            box-shadow: none !important;
                            border: none !important;
                            background-color: #fff !important;
                        }
                    }
                `}</style>

                {/* Print Toolbar */}
                <Box className="no-print" paddingBottom="medium" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <select
                        value={printLang}
                        onChange={(e) => setPrintLang(e.target.value as Lang)}
                        style={{
                            padding: "4px 8px",
                            fontSize: "13px",
                            borderRadius: "4px",
                            border: "1px solid #c8ccd4",
                            backgroundColor: "#fff",
                            color: "#2b303a",
                            outline: "none",
                            cursor: "pointer",
                            height: "32px",
                            fontWeight: 500,
                        }}
                    >
                        <option value="en">English (EN)</option>
                        <option value="si">Sinhala (SI)</option>
                    </select>
                    <Actions>
                        <Button
                            variant="solid"
                            tone="brandAccent"
                            icon={<IconPrint />}
                            onClick={() => window.print()}
                        >
                            {t("printButton")}
                        </Button>
                    </Actions>
                </Box>

                {/* Letter Canvas */}
                <Box display="flex" justifyContent="center">
                    <div
                        id="print-page"
                        className="print-area"
                        style={{
                            width: "21cm",
                            minHeight: "29.7cm",
                            backgroundColor: "#ffffff",
                            boxShadow: "0 0 10px rgba(0,0,0,0.08)",
                            border: "1px solid #e2e8f0",
                            borderRadius: "4px",
                            padding: "1.2cm 1.4cm 1.2cm 1.5cm",
                            boxSizing: "border-box",
                            fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Times New Roman', 'Iskoola Pota', serif",
                            fontSize: "14px",
                            color: "#1e293b",
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "14px", borderBottom: "3px solid #1e293b", paddingBottom: "10px" }}>
                            {logoUrl && <img src={logoUrl} alt="Institute Logo" style={{ height: "70px", width: "auto", marginRight: "14px" }} />}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "18px", fontWeight: "bold", textAlign: "center", textTransform: "uppercase" }}>{instName}</div>
                                {instAddress && <div style={{ fontSize: "14px", textAlign: "center", color: "#475569", marginTop: "2px" }}>{instAddress}</div>}
                            </div>
                        </div>

                        {/* Title */}
                        <div style={{ textAlign: "center", fontSize: "15px", fontWeight: "bold", margin: "4px 0 16px 0", color: "#1e293b" }}>
                            {t("title")}
                        </div>

                        {/* Customer Block */}
                        <table style={{ width: "auto", borderCollapse: "collapse", fontSize: "14px", marginBottom: "16px" }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: "3px 8px 3px 0", fontWeight: "bold", whiteSpace: "nowrap" }}>{t("memberNumber")}:</td>
                                    <td style={{ padding: "3px 0" }}>{customer.customer_number || "-"}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "3px 8px 3px 0", fontWeight: "bold", whiteSpace: "nowrap" }}>{t("customerName")}:</td>
                                    <td style={{ padding: "3px 0" }}>{custName || "-"}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: "3px 8px 3px 0", fontWeight: "bold", whiteSpace: "nowrap" }}>{t("address")}:</td>
                                    <td style={{ padding: "3px 0" }}>{custAddress || "-"}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div style={{ marginBottom: "12px" }}>{t("salutation")}</div>

                        {/* Previous FD Details */}
                        <div style={{ fontSize: "13px", fontWeight: "bold", borderBottom: "2px solid #1e293b", paddingBottom: "4px", marginBottom: "8px" }}>
                            {t("prevHeader")}
                        </div>
                        {renderDetailTable([
                            { label: t("branch"), value: branchName || "-" },
                            { label: t("accountNo"), value: prev.account_number || "-" },
                            { label: t("depositAmount"), value: formatCurrency(prev.deposit_amount) },
                            { label: t("maturityDate"), value: fmtDate(prev.maturity_date) },
                            { label: t("prevRate"), value: prev.interest_rate != null ? `${prev.interest_rate}%` : "-" },
                            { label: t("period"), value: prev.period != null ? String(prev.period) : "-" },
                            { label: t("totalInterest"), value: formatCurrency(prev.interest) },
                            { label: t("withholdingTax"), value: formatCurrency(prev.tax) },
                            { label: t("netInterest"), value: formatCurrency(prev.net_interest) },
                        ])}

                        <div style={{ margin: "4px 0 14px 0", lineHeight: "1.6", textAlign: "justify" }}>{t("introParagraph")}</div>

                        {/* Renewed FD Details */}
                        <div style={{ fontSize: "13px", fontWeight: "bold", borderBottom: "2px solid #1e293b", paddingBottom: "4px", marginBottom: "8px" }}>
                            {t("renewHeader")}
                        </div>
                        {renderDetailTable([
                            { label: t("currentValue"), value: formatCurrency(renewal.current_value) },
                            { label: t("newAccountNo"), value: renewal.account_number || "-" },
                            { label: t("renewPeriod"), value: renewal.period != null ? String(renewal.period) : "-" },
                            { label: t("newRate"), value: renewal.interest_rate != null ? `${renewal.interest_rate}%` : "-" },
                            { label: t("interestAtMaturity"), value: formatCurrency(renewal.interest_at_maturity) },
                            { label: t("renewDate"), value: fmtDate(renewal.renew_date) },
                            { label: t("branchTelephone"), value: renewal.branch_telephone || "-" },
                        ])}

                        {/* Tax Paragraph */}
                        <div style={{ margin: "4px 0 24px 0", lineHeight: "1.6", textAlign: "justify" }}>
                            {t("taxParagraphPrefix")}{formatCurrency(taxAmount)}{t("taxParagraphSuffix")}
                        </div>

                        {/* Signature */}
                        <div style={{ marginTop: "60px" }}>
                            <div style={{ width: "45%" }}>
                                <div style={{ marginBottom: "4px", whiteSpace: "pre-line" }}>{t("yoursFaithfully")}</div>
                                <div style={{ borderBottom: "1px solid #1e293b", height: "40px" }}></div>
                                <div style={{ marginTop: "4px", fontWeight: "bold", textAlign: "center" }}>{t("manager")}</div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px dashed #cbd5e1", marginTop: "40px", paddingTop: "8px", fontSize: "10px", color: "#475569" }}>
                            <div>{instName}</div>
                            <div>{t("page")} 1 {t("of")} 1</div>
                            <div>{userName ? `${t("user")}: ${userName}` : todayStr}</div>
                        </div>
                    </div>
                </Box>
            </Box>
        </>
    );
};

export default FdRenewalLetterPrint;